import { CancelTokenSource } from 'axios';

import {
  AccessorialCode,
  CarrierSetting,
  DomainBranding,
  DomainTree,
  IShipmentStop,
  ServiceIncidentType,
} from '@rygen/rygen-ui/model';

import {
  CarrierShipmentSummary,
  DocumentProperties,
  DocumentReference,
  DocumentUploadUrlResponse,
  DriverContact,
  RateQuote,
  ShipmentEvent,
  ShipmentServiceIncident,
  ShipmentStop,
  SignedDocument,
  SpotMarket,
  TenderAcceptRequest,
  TenderRejectRequest,
  UpdateExpectedArrivalRequest,
} from '@/model';
import { MapDetailsResponse } from '@/model/geocoding/MapDetailsResponse';
import { buildShipmentEvent } from '@/util';

import { BaseHttpController } from './BaseHttpController';

export type GetShipmentSummaryParams = {
  accessToken: string;
  withTenderRequest: boolean;
};

export class CarrierPortalController extends BaseHttpController {
  static readonly ENDPOINT: string = 'carrier-portal';

  constructor() {
    super(CarrierPortalController.ENDPOINT, { bypassAuthInterceptor: true });
  }

  async acceptTender(
    shipmentId: number,
    tenderAcceptRequest: TenderAcceptRequest,
    accessToken: string,
  ): Promise<CarrierShipmentSummary> {
    const response = await this.http.post<CarrierShipmentSummary>(
      `shipment/${shipmentId}/tender/accept`,
      tenderAcceptRequest,
      { params: { accessToken } },
    );
    return new CarrierShipmentSummary(response.data);
  }

  async rejectTender(
    shipmentId: number,
    tenderRejectRequest: TenderRejectRequest,
    accessToken: string,
  ): Promise<CarrierShipmentSummary> {
    const response = await this.http.post<CarrierShipmentSummary>(
      `shipment/${shipmentId}/tender/reject`,
      tenderRejectRequest,
      { params: { accessToken } },
    );
    return new CarrierShipmentSummary(response.data);
  }

  async getShipmentSummary(
    shipmentId: number,
    params: GetShipmentSummaryParams,
  ): Promise<CarrierShipmentSummary> {
    const response = await this.http.get<CarrierShipmentSummary>(
      `shipment/${shipmentId}`,
      { params },
    );
    return new CarrierShipmentSummary(response.data);
  }

  async getShipmentSpecSummary(
    shipmentSpecId: number,
    params: GetShipmentSummaryParams,
  ): Promise<CarrierShipmentSummary> {
    const response = await this.http.get<CarrierShipmentSummary>(
      `shipment-spec/${shipmentSpecId}`,
      { params },
    );
    return new CarrierShipmentSummary(response.data);
  }

  async loadDriverContactForShipment(
    shipmentId: number,
    accessToken: string,
  ): Promise<DriverContact> {
    const response = await this.http.get<DriverContact>(
      `shipment/${shipmentId}/driver-contact`,
      { params: { accessToken } },
    );
    return new DriverContact(response.data);
  }

  async createDriverContactForShipment(
    shipmentId: number,
    driverContact: DriverContact,
    accessToken: string,
  ): Promise<DriverContact> {
    if (driverContact.shipmentId !== shipmentId) {
      throw new Error(
        'driverContact.shipment should be the same as shipmentId',
      );
    }

    const response = await this.http.post<DriverContact>(
      `shipment/${shipmentId}/driver-contact`,
      { shipmentId, driverContact, accessToken },
    );
    return new DriverContact(response.data);
  }

  async updateDriverContactForShipment(
    shipmentId: number,
    driverContact: DriverContact,
    accessToken: string,
  ): Promise<DriverContact> {
    if (!driverContact.id) {
      throw new Error('Id is required to perform update');
    }

    if (driverContact.shipmentId !== shipmentId) {
      throw new Error(
        'driverContact.shipment should be the same as shipmentId',
      );
    }

    const response = await this.http.put<DriverContact>(
      `shipment/${shipmentId}/driver-contact/${driverContact.id}`,
      { shipmentId, driverContact, accessToken },
    );
    return new DriverContact(response.data);
  }

  async getDomainTree(
    domainId: number,
    accessToken: string,
  ): Promise<Array<DomainTree>> {
    const response = await this.http.get<Array<DomainTree>>(
      `domain/${domainId}`,
      { params: { accessToken } },
    );

    return response.data.map((d) => new DomainTree(d));
  }

  async getDomainBranding(
    domainId: number,
    accessToken: string,
  ): Promise<DomainBranding> {
    const response = await this.http.get<DomainBranding>(
      `domain/${domainId}/branding`,
      { params: { accessToken } },
    );
    return new DomainBranding(response.data);
  }

  async getDocumentsByShipmentId(
    shipmentId: number,
    accessToken: string,
  ): Promise<SignedDocument[]> {
    const response = await this.http.get<SignedDocument[]>(
      `shipment/${shipmentId}/document`,
      { params: { accessToken } },
    );
    return response.data.map((d) => new SignedDocument(d));
  }

  async getUploadUrl(
    shipmentId: number,
    documentReference: DocumentReference,
    accessToken: string,
  ): Promise<DocumentUploadUrlResponse> {
    const response = await this.http.post<DocumentUploadUrlResponse>(
      `shipment/${shipmentId}/document/upload-url`,
      { ...documentReference, accessToken },
    );
    return new DocumentUploadUrlResponse(response.data);
  }

  async attachDocument(
    shipmentId: number,
    documentProperties: DocumentProperties,
    accessToken: string,
  ): Promise<SignedDocument> {
    const response = await this.http.post<SignedDocument>(
      `shipment/${shipmentId}/document?accessToken=${accessToken}`,
      documentProperties,
    );
    return new SignedDocument(response.data);
  }

  async removeDocument(
    shipmentId: number,
    documentId: number,
    accessToken: string,
  ): Promise<void> {
    await this.http.delete<void>(
      `shipment/${shipmentId}/document/${documentId}`,
      { params: { accessToken } },
    );
  }

  async createEvent(
    shipmentId: number,
    shipmentEvent: ShipmentEvent,
    accessToken: string,
  ): Promise<ShipmentEvent> {
    const response = await this.http.post<ShipmentEvent>(
      `shipment/${shipmentId}/event`,
      {
        shipmentId,
        shipmentEvent,
        accessToken,
      },
    );
    return buildShipmentEvent(response.data);
  }

  async updateEvent(
    shipmentId: number,
    shipmentEvent: ShipmentEvent,
    accessToken: string,
  ): Promise<ShipmentEvent> {
    const response = await this.http.put<ShipmentEvent>(
      `shipment/${shipmentId}/event`,
      {
        shipmentId,
        shipmentEvent,
        accessToken,
      },
    );
    return buildShipmentEvent(response.data);
  }

  async loadServiceIncidentTypes(
    shipmentId: number,
    accessToken: string,
  ): Promise<ServiceIncidentType[]> {
    const response = await this.http.get<ServiceIncidentType[]>(
      `shipment/${shipmentId}/incident/type`,
      { params: { accessToken } },
    );
    return response.data.map((t) => new ServiceIncidentType(t));
  }

  async createServiceIncident(
    shipmentId: number,
    serviceIncident: ShipmentServiceIncident,
    accessToken: string,
  ): Promise<ShipmentServiceIncident> {
    const response = await this.http.post<ShipmentServiceIncident>(
      `shipment/${shipmentId}/incident`,
      {
        shipmentId,
        serviceIncident,
        accessToken,
      },
    );
    return new ShipmentServiceIncident(response.data);
  }

  async updateExpectedArrival(
    shipmentId: number,
    updateRequest: UpdateExpectedArrivalRequest,
    accessToken: string,
  ): Promise<ShipmentStop> {
    const shipmentStopId = updateRequest.relatedStop?.id;
    if (!shipmentStopId || shipmentStopId === -1) {
      throw new Error('Related stop id is required');
    }

    const response = await this.http.put<ShipmentStop>(
      `shipment/${shipmentId}/stop/${shipmentStopId}/expected-arrival`,
      {
        shipmentId,
        shipmentStopId,
        expectedArrival: updateRequest.expectedArrival,
        comment: updateRequest.comment,
        accessToken,
      },
    );
    return new ShipmentStop(response.data);
  }

  async cancelCheckCallReminders(
    shipmentId: number,
    accessToken: string,
  ): Promise<void> {
    await this.http.post<void>(`shipment/${shipmentId}/check-call/cancel`, {
      shipmentId,
      accessToken,
    });
  }

  async findCarrierSettingsForScacAndShipment(
    scac: string,
    shipmentId: number,
    accessToken: string,
  ): Promise<CarrierSetting> {
    const response = await this.http.get<CarrierSetting>(
      `shipment/${shipmentId}/carrier/${scac}/client-config`,
      { params: { accessToken } },
    );
    return new CarrierSetting(response.data);
  }

  async findCarrierSettingsForScacAndShipmentSpec(
    scac: string,
    shipmentSpecId: number,
    accessToken: string,
  ): Promise<CarrierSetting> {
    const response = await this.http.get<CarrierSetting>(
      `shipment-spec/${shipmentSpecId}/carrier/${scac}/client-config`,
      { params: { accessToken } },
    );
    return new CarrierSetting(response.data);
  }

  async findAllRequestableAccessorialCodesByShipmentId(
    shipmentId: number,
    accessToken: string,
  ): Promise<AccessorialCode[]> {
    const response = await this.http.get<AccessorialCode[]>(
      `shipment/${shipmentId}/accessorial-code/requestable`,
      { params: { accessToken } },
    );
    return response.data.map((val) => new AccessorialCode(val));
  }

  async findAllRequestableAccessorialCodesByShipmentSpecId(
    shipmentSpecId: number,
    accessToken: string,
  ): Promise<AccessorialCode[]> {
    const response = await this.http.get<AccessorialCode[]>(
      `shipment-spec/${shipmentSpecId}/accessorial-code/requestable`,
      { params: { accessToken } },
    );
    return response.data.map((val) => new AccessorialCode(val));
  }

  async loadSpotMarketForShipmentSpec(
    shipmentSpecId: number,
    carrierId: number,
    accessToken: string,
  ): Promise<SpotMarket> {
    const response = await this.http.get<SpotMarket>(
      `shipment-spec/${shipmentSpecId}/spot-market`,
      { params: { carrierId, accessToken } },
    );
    return new SpotMarket(response.data);
  }

  async rejectSpotMarketForShipmentSpec(
    shipmentSpecId: number,
    spotMarketId: number,
    accessToken: string,
  ): Promise<void> {
    await this.http.post<void>(
      `shipment-spec/${shipmentSpecId}/spot-market/${spotMarketId}/reject`,
      { shipmentSpecId, spotMarketId, accessToken },
    );
  }

  async logFirstSpotMarketVisitForShipmentSpec(
    shipmentSpecId: number,
    spotMarketId: number,
    accessToken: string,
  ): Promise<void> {
    await this.http.post<void>(
      `shipment-spec/${shipmentSpecId}/spot-market/${spotMarketId}/first-visit`,
      { shipmentSpecId, spotMarketId, accessToken },
    );
  }

  async addSpotMarketBidForShipmentSpec(
    shipmentSpecId: number,
    spotMarketId: number,
    spotMarketBid: RateQuote,
    accessToken: string,
  ): Promise<RateQuote> {
    const response = await this.http.post<RateQuote>(
      `shipment-spec/${shipmentSpecId}/spot-market/${spotMarketId}/bid`,
      { shipmentSpecId, spotMarketId, spotMarketBid, accessToken },
    );
    return new RateQuote(response.data);
  }

  async deleteSpotMarketBidForShipmentSpec(
    shipmentSpecId: number,
    spotMarketId: number,
    bidId: number,
    accessToken: string,
  ): Promise<void> {
    await this.http.delete<void>(
      `shipment-spec/${shipmentSpecId}/spot-market/${spotMarketId}/bid/${bidId}`,
      { params: { accessToken } },
    );
  }

  async loadSpotMarketForShipment(
    shipmentId: number,
    carrierId: number,
    accessToken: string,
  ): Promise<SpotMarket> {
    const response = await this.http.get<SpotMarket>(
      `shipment/${shipmentId}/spot-market`,
      { params: { carrierId, accessToken } },
    );
    return new SpotMarket(response.data);
  }

  async rejectSpotMarketForShipment(
    shipmentId: number,
    spotMarketId: number,
    accessToken: string,
  ): Promise<void> {
    const response = await this.http.post<void>(
      `shipment/${shipmentId}/spot-market/${spotMarketId}/reject`,
      { shipmentId, spotMarketId, accessToken },
    );
    return response.data;
  }

  async logFirstSpotMarketVisitForShipment(
    shipmentId: number,
    spotMarketId: number,
    accessToken: string,
  ): Promise<void> {
    await this.http.post<void>(
      `shipment/${shipmentId}/spot-market/${spotMarketId}/first-visit`,
      { shipmentId, spotMarketId, accessToken },
    );
  }

  async addSpotMarketBidForShipment(
    shipmentId: number,
    spotMarketId: number,
    spotMarketBid: RateQuote,
    accessToken: string,
  ): Promise<RateQuote> {
    const response = await this.http.post<RateQuote>(
      `shipment/${shipmentId}/spot-market/${spotMarketId}/bid`,
      { shipmentId, spotMarketId, spotMarketBid, accessToken },
    );

    return new RateQuote(response.data);
  }

  async deleteSpotMarketBidForShipment(
    shipmentId: number,
    spotMarketId: number,
    bidId: number,
    accessToken: string,
  ): Promise<void> {
    await this.http.delete<void>(
      `shipment/${shipmentId}/spot-market/${spotMarketId}/bid/${bidId}`,
      { params: { accessToken } },
    );
  }

  async getMapDetails(
    shipmentId: number,
    stops: IShipmentStop[],
    events: ShipmentEvent[],
    accessToken: string,
    cancelToken?: CancelTokenSource,
  ): Promise<MapDetailsResponse> {
    const response = await this.http.post<MapDetailsResponse>(
      `shipment/${shipmentId}/route/map-details`,
      { shipmentId, stops, events, accessToken },
      { cancelToken: cancelToken?.token },
    );

    return new MapDetailsResponse(response.data);
  }
}
